import { Page, PagesPermissions } from "../types/Authorization";
import { isPermissionGranted } from "./navPermissions";
import { priceSheetsFor } from "./priceSheets";
import { handOverPriceList, startPriceList } from "./priceListHandoff";
import { handOverDraft } from "./draftHandoff";
import { AskPageKind } from "./assistant";
import { AttachmentRead, LocalVerdict, classifyLocally, documentRow, missionRefFromWords, nameFromWords, wantsNewRecord } from "./askAttachment";
import { readDetails } from "./companyDetails";
import { RecordKind, SimilarRecord } from "./similarRecord";
import { AfterCreateStep, queueAfterCreate } from "./afterCreate";
import { fileKind } from "./priceList";
import type { ProposedPriceLine } from "./priceList";
import type { FleetItem } from "./fleetList";
import type { SessionList } from "./creationSession";
import { parseRecordIntent } from "./recordIntent";

/**
 * What Ask does with an attached file (roadmap: "Attach — read it, know the
 * context, do what's needed"). Decided by code from what the file is, the
 * person's words, the page they're on and their rights. Nothing is ever
 * saved here: each outcome is an action the person clicks (a form or a
 * sheet opens filled in, unsaved) or a question to Ask, which prepares drafts.
 */

export interface AttachTarget {
    kind: "mission" | "client" | "provider";
    id: number;
    label: string;
}

export type AttachGo =
    | { type: "contact"; text?: string; file?: File; organizationName?: string }
    | { type: "lead"; text: string }
    | { type: "price_list_text"; text: string; sheet?: "provider"; recordId?: number }
    | { type: "record_update"; kind: RecordKind; id: number; text?: string; file?: File }
    | { type: "fleet_text"; providerId: number; text: string }
    | { type: "fleet_file"; providerId: number; file: File; items?: FleetItem[] }
    | { type: "record_new"; kind: "provider" | "client" | "company"; text?: string; file?: File; name?: string; then?: AfterCreateStep[]; address?: { label: string; country: unknown; city: unknown }; notes?: string; phone?: string; email?: string; website?: string }
    | { type: "price_list"; file: File; providerId?: number; lines?: ProposedPriceLine[]; places?: string[] }
    | { type: "booking"; missionId: number; file: File }
    | { type: "document"; target: AttachTarget; row: ReturnType<typeof documentRow> }
    | { type: "link"; to: string };

export interface AttachAction {
    label: string;
    icon: string;
    title: string;
    go: AttachGo;
}

export interface AttachPlan {
    /** Ask's reply, shown at once (no message used). */
    message: string;
    actions: AttachAction[];
    /** Ask the assistant with what was read (one message). */
    chat?: { question: string; details: string };
    /** The read failed: keep the file in the box to try again. */
    keepFile?: boolean;
    /** Read, but where it goes isn't known yet: the person's next words can name it (no second read). */
    awaitsTarget?: { file: File; read: AttachmentRead; localText?: string };
    /** The AI reader was used. */
    usedReader?: boolean;
    /** The plan found where the file goes (the record on screen or the one named). */
    targeted?: boolean;
    /** A new provider for the list read: Ask's creation conversation gathers what it needs (code only). */
    session?: SessionList;
    /** An ID document: it never leaves this computer. */
    sensitive?: boolean;
    /** A document (PDF, Word, Excel, CSV, text) Ask can't act on itself: the file goes whole with the question; the server reads and masks it. */
    material?: boolean;
}

export interface AttachInput {
    file: File;
    words: string;
    page: { kind: AskPageKind; id: number } | null;
    pages: Page[];
    documentReading: boolean;
    /** The AI reader (POST /ai/attachments/read): the read, or an error message. */
    readOnServer: (file: File, words: string) => Promise<{ read: AttachmentRead } | { error: string }>;
    /** A mission reference → the mission, as the person can see it (null if not). */
    findMission: (reference: string) => Promise<AttachTarget | null>;
    /** The provider, client, company or lead a name points to, as the person can see it (null if none). */
    findRecord?: (name: string, kinds: RecordKind[]) => Promise<SimilarRecord | null>;
    /** Local reading (injected for tests). */
    classify?: (file: File, words: string) => Promise<LocalVerdict>;
}

export const ATTACH_DEFAULT = "Use the attached file to prepare what's needed.";

/** The longest question Ask sends as a question (AiChatService MAX_QUESTION); a longer paste goes as material, read whole on the server. */
export const ASK_MAX_QUESTION = 1500;

const ID_MESSAGE = "This looks like a passport or ID card. It stayed on this computer — ID documents never go to the AI. To use it, open the traveller's contact and choose Read passport.";

const contactAction = (go: AttachGo): AttachAction => ({
    label: "Fill a new contact", icon: "person-vcard", go,
    title: "Open a new contact with Fill with AI reading the card on this computer — you tick what goes in and save.",
});

const DOC_TARGET_RIGHT: Record<AttachTarget["kind"], string> = {
    mission: PagesPermissions.MISSIONS_EDIT,
    client: PagesPermissions.CLIENTS_EDIT,
    provider: PagesPermissions.PROVIDERS_EDIT,
};

/**
 * Plan what to do with an attached file.
 * @returns The reply and the actions to offer — or a question for Ask.
 */
export const planAttachment = async (input: AttachInput): Promise<AttachPlan> => {
    const { file, words, pages } = input;
    const can = (right: string) => isPermissionGranted(pages, right);
    const verdict = await (input.classify ?? classifyLocally)(file, words).catch((): LocalVerdict => ({ kind: "unsupported", reason: "Couldn't open that file." }));

    switch (verdict.kind) {
        case "unsupported":
            return { message: verdict.reason, actions: [] };
        case "identity_document":
            return { message: ID_MESSAGE, actions: [], sensitive: true };
        case "contact_card":
            return can(PagesPermissions.CONTACTS_CREATE)
                ? { message: "This looks like a contact card. I read it on this computer — nothing was sent.", actions: [contactAction({ type: "contact", text: verdict.text })] }
                : { message: "This looks like a contact card.", actions: [] };
        case "price_list":
        case "fleet_list":
            // a spreadsheet read here: it goes to the provider on screen, or the one the words name
            return planFromRead({ kind: verdict.kind, title: null, documentType: null, validFrom: null, validTo: null, missionReference: null, trip: null, details: null }, file, words, input, false);
        case "text":
            return { message: "", actions: [], material: true };
        default:
            break;
    }

    // A PDF or Word file: it goes whole with the question; the server reads and masks it (no documents rule).
    if (fileKind(file) !== "image") return { message: "", actions: [], material: true };
    // A photo the code couldn't place: the AI reader (documents rule).
    if (!input.documentReading) {
        return { message: "Reading documents with AI is switched off in the AI settings, so I can't tell what this is.", actions: [] };
    }
    const result = await input.readOnServer(file, words);
    if ("error" in result) return { message: result.error, actions: [], keepFile: true };
    return planFromRead(result.read, file, words, input, true, verdict.kind === "server" ? verdict.text : undefined);
};

/** Company documents whose details belong on the record itself (not only in its documents). */
const COMPANY_DOCUMENTS = new Set(["license", "permit", "certificate", "company_profile", "letter"]);
const RECORD_EDIT_RIGHT: Record<RecordKind, string> = {
    contact: PagesPermissions.CONTACTS_EDIT,
    provider: PagesPermissions.PROVIDERS_EDIT,
    client: PagesPermissions.CLIENTS_EDIT,
    company: PagesPermissions.COMPANIES_EDIT,
    lead: PagesPermissions.LEADS_EDIT,
};
const nameOf = (record: SimilarRecord) => `${record.name}${record.exact ? "" : " (predicted)"}`;

/**
 * What to offer for a file the AI reader has read: the action for its kind,
 * aimed at the record on screen or the one the words name.
 * @param read - What the reader reported.
 * @param usedReader - This plan used the reader (a credit); false for a follow-up.
 * @param localText - What OCR read on this computer (photos): names the company it belongs to.
 */
export const planFromRead = async (read: AttachmentRead, file: File, words: string, input: Omit<AttachInput, "file" | "words">, usedReader: boolean, localText?: string): Promise<AttachPlan> => {
    const { page, pages } = input;
    const can = (right: string) => isPermissionGranted(pages, right);
    const question = words.trim() || ATTACH_DEFAULT;
    const awaitsTarget = { file, read, localText };
    const what =read.title ?? read.kind.replace(/_/g, " ");

    const missionTarget = async (): Promise<AttachTarget | null> => {
        if (page?.kind === "mission") return { kind: "mission", id: page.id, label: "this mission" };
        const reference = missionRefFromWords(words) ?? read.missionReference;
        return reference ? input.findMission(reference) : null;
    };
    // The record the person's words, the document's own text (read here) or its title name — searched as the person.
    const namedRecord = async (kinds: RecordKind[]): Promise<SimilarRecord | null> => {
        if (!input.findRecord) return null;
        const candidates = [nameFromWords(words), words.trim(), localText ? readDetails(localText).company : null, read.title]
            .filter((candidate): candidate is string => !!candidate && candidate.length >= 3);
        for (const candidate of candidates) {
            const record = await input.findRecord(candidate, kinds).catch(() => null);
            if (record) return record;
        }
        return null;
    };
    // The supplier of a price or fleet list: the provider it names; the provider on screen only when the
    // list is its (it names that provider, or names no company we could read) — never another company's list.
    const readCompany = localText ? readDetails(localText).company : null;
    const supplier = async (): Promise<{ id: number; named: SimilarRecord | null } | null> => {
        const named = await namedRecord(["provider"]);
        if (page?.kind === "provider") {
            if (named && named.id !== page.id) return { id: named.id, named };
            return named || !readCompany ? { id: page.id, named: null } : null;
        }
        return named ? { id: named.id, named } : null;
    };

    switch (read.kind) {
        case "identity_document":
            return { message: ID_MESSAGE, actions: [], usedReader };
        case "price_list":
        case "fleet_list": {
            const isFleet = read.kind === "fleet_list";
            const lines = read.priceLines?.length ? read.priceLines.map((line) => ({ ...line, context: line.context ?? read.title ?? undefined })) : undefined;
            const items = read.fleetItems?.length ? read.fleetItems : undefined;
            const target = await supplier();
            const named = target?.named ?? null;
            const providerId = target?.id ?? null;
            // the name the person gave ("create new provider ABC", "the provider name is ABC")
            const name = words.trim() ? nameFromWords(words) : null;
            const asksNew = wantsNewRecord(words);
            const what = isFleet ? "assets open with this list" : "rate sheet opens with this list";
            const useIt: AttachAction | null = providerId && can(PagesPermissions.PROVIDERS_EDIT) && (isFleet || can(PagesPermissions.PROVIDERS_FINANCE)) ? {
                label: `Fill with AI: ${named ? `${nameOf(named)}'s` : "this provider's"} ${isFleet ? "fleet" : "rates"}`, icon: "stars",
                go: isFleet ? { type: "fleet_file", providerId, file, items } : { type: "price_list", file, providerId, lines },
                title: `Open the provider's ${isFleet ? "assets" : "rate sheet"} with this list ready to read — you tick each line; nothing is saved until you save.${named && !named.exact ? " The provider is the closest match to the name read — check it's the right one." : ""}`,
            } : null;
            // a new provider (named as the person said): its sheet opens with the list once it's saved
            const startIt: AttachAction | null = (!providerId || asksNew) && can(PagesPermissions.PROVIDERS_CREATE) ? {
                label: `Fill with AI: new provider${name ? ` "${name}"` : ""} + ${isFleet ? "fleet" : "rates"}`, icon: "stars",
                go: { type: "record_new", kind: "provider", name: name ?? undefined, then: [isFleet ? { type: "fleet_list", file, items } : { type: "price_list", file, lines }] },
                title: `Open a new provider${name ? ` named ${name}` : ""} — fill in its details and save; its ${what} to read.`,
            } : null;
            const picker: AttachAction | null = !isFleet && priceSheetsFor(pages).length
                ? { label: "Fill with AI: price sheet", icon: "stars", go: { type: "price_list", file, lines }, title: "Pick the provider, request or mission sheet; the lines open there to tick and correct. Nothing is saved until you save the sheet." }
                : null;
            const kindName = isFleet ? "fleet list" : "price list";
            // a new provider asked for, one named that CRX doesn't have, or where it's based or works said (not
            // about the provider on screen): Ask's creation conversation asks for what's missing, then the form
            const intent = parseRecordIntent(words);
            const placed = !!(intent.base || intent.coverage.length) && !(page?.kind === "provider" && providerId === page.id);
            if (can(PagesPermissions.PROVIDERS_CREATE) && (asksNew || placed || (name && !providerId))) {
                return { message: "", actions: [], usedReader, targeted: true, session: { kind: read.kind as SessionList["kind"], file, lines, items, title: read.title ?? null, named: readCompany } };
            }
            if (asksNew && startIt) {
                // asked for a new one: that first — unless CRX already has it, then say so (no duplicate by accident)
                const message = named
                    ? `${named.exact ? `${named.name} is already in CRX` : `Closest in CRX: ${named.name} (predicted)`} — put the ${kindName} there, or start ${name ? `"${name}"` : "a new provider"} anyway.`
                    : `New provider${name ? ` "${name}"` : ""}: open it, fill in its details and save — its ${what}.`;
                const actions = (named ? [useIt, startIt, picker] : [startIt, picker]).filter((action): action is AttachAction => !!action);
                return { message, actions, usedReader, targeted: true };
            }
            const actions = [useIt ?? startIt, picker].filter((action): action is AttachAction => !!action);
            if (providerId) return { message: `This is a ${kindName}${read.title ? ` (${read.title})` : ""}.`, actions, usedReader, targeted: true };
            // a name typed after "whose is it?" that CRX doesn't have: said here (no AI), with the way to add it
            if (name) return { message: `No provider called "${name}" in CRX that you can open. Add it as a new provider — its ${what} once it's saved${picker ? " — or pick another sheet" : ""}.`, actions, usedReader, targeted: true };
            if (isFleet && !can(PagesPermissions.PROVIDERS_EDIT)) return { message: "This is a fleet list.", actions: [], usedReader };
            return {
                message: isFleet
                    ? "This is a fleet list. Which provider is it for? Type their name, or open the provider and attach it there."
                    : `This is a price list${read.title ? ` (${read.title})` : ""}. Whose is it? Type the provider name, or pick the sheet.`,
                actions, usedReader, awaitsTarget,
            };
        }
        case "contact_card":
            return can(PagesPermissions.CONTACTS_CREATE)
                ? { message: "This is a contact card. It's read on the contact page, on this computer — names never come from the AI.", actions: [contactAction({ type: "contact", file })], usedReader }
                : { message: "This is a contact card.", actions: [], usedReader };
        case "receipt": {
            const mission = missionRefFromWords(words) || page?.kind === "mission" ? await missionTarget() : null;
            const claims: AttachAction = { label: "Open My HR", icon: "box-arrow-up-right", go: { type: "link", to: "/people/my" }, title: "Your expense claims are in My HR" };
            if (mission) {
                return {
                    message: `This is a receipt${read.title ? ` (${read.title})` : ""} for ${mission.label}. Mission expenses go on the movement they were spent on: open the movement › Expenses, add the line and attach this receipt. To claim it back yourself, add it to an expense claim in My HR.`,
                    targeted: true,
                    actions: [{ label: `Open ${mission.label}`, icon: "box-arrow-up-right", go: { type: "link", to: `/mission/${mission.id}` }, title: "Open the mission — its movements carry the expenses" }, claims],
                    usedReader,
                };
            }
            return { message: `This is a receipt${read.title ? ` (${read.title})` : ""}. Is it a mission expense? Type the mission reference. To claim it back yourself, open the claim in My HR and use Fill with AI on the expense.`, actions: [claims], awaitsTarget, usedReader };
        }
        case "supplier_invoice":
            return { message: `This is a supplier invoice${read.title ? ` (${read.title})` : ""}. Open the purchase it belongs to and use Fill with AI there.`, actions: [], usedReader };
        case "booking": {
            const target = await missionTarget();
            if (target && can(PagesPermissions.MISSIONS_EDIT)) {
                return {
                    message: `This is a booking${read.title ? ` (${read.title})` : ""}.`,
                    targeted: true,
                    actions: [{ label: `Add its flights to ${target.label}`, icon: "airplane", go: { type: "booking", missionId: target.id, file }, title: "Open the mission's movements with this booking ready to read — you tick each flight and save." }],
                    usedReader,
                };
            }
            if (read.details) return { message: "", actions: [], chat: { question, details: read.details }, usedReader };
            return { message: `This is a booking. Which mission is it for? Type its reference (e.g. "MSN-0142"), or open the mission and attach it there.`, actions: [], awaitsTarget, usedReader };
        }
        case "record_document": {
            const fromPage: AttachTarget | null = page && (page.kind === "mission" || page.kind === "client" || page.kind === "provider")
                ? { kind: page.kind, id: page.id, label: `this ${page.kind}` }
                : null;
            const row = documentRow(file, read);
            const until = read.validTo ? `, valid to ${read.validTo}` : "";
            const keep = (target: AttachTarget): AttachAction => ({ label: `Keep it on ${target.label}`, icon: "file-earmark-plus", go: { type: "document", target, row }, title: `Add it to the documents as ${row.type}${until} — the documents open to check, then you save.` });
            const isCompanyDocument = COMPANY_DOCUMENTS.has(String(read.documentType ?? ""));
            // the record the document names (words, its text read here, its title) — searched as the person
            const named = missionRefFromWords(words) ? null : await namedRecord(["provider", "client", "company", "lead"]);
            const isPageRecord = !!named && !!fromPage && named.kind === fromPage.kind && named.id === fromPage.id;
            const actions: AttachAction[] = [];
            // a licence, profile or letter updates only the record it names — never the one on screen just because you're on it
            if (named && isCompanyDocument && can(RECORD_EDIT_RIGHT[named.kind])) {
                const label = isPageRecord ? `this ${named.kind}` : nameOf(named);
                actions.push({ label: `Fill with AI: update ${label}`, icon: "stars", go: { type: "record_update", kind: named.kind, id: named.id, file }, title: `Open ${isPageRecord ? label : named.name} with Fill with AI reading this document — each new or changed detail shows as current → new to tick; nothing is saved until you save.${named.exact ? "" : " The record is the closest match to the name read — check it's the right one."}` });
            }
            // kept on the record it names; else the one on screen or the mission the words name
            const keepOn: AttachTarget | null = named && !isPageRecord
                ? (named.kind === "provider" || named.kind === "client" ? { kind: named.kind, id: named.id, label: nameOf(named) } : null)
                : fromPage ?? await missionTarget();
            if (keepOn && can(DOC_TARGET_RIGHT[keepOn.kind])) actions.push(keep(keepOn));
            // a company that isn't in CRX yet: start it — the new form reads this document (company details only)
            if (!named && isCompanyDocument) {
                const wanted = /\b(client|customer)\b/i.test(words) ? "client" : "provider";
                const starts = (["provider", "client"] as const)
                    .filter((kind) => can(kind === "provider" ? PagesPermissions.PROVIDERS_CREATE : PagesPermissions.CLIENTS_CREATE))
                    .sort((a, b) => Number(b === wanted) - Number(a === wanted));
                const typed = words.trim() ? nameFromWords(words) : null;
                if (starts.length) {
                    const newOnes: AttachAction[] = starts.map((kind) => ({
                        label: `Fill with AI: new ${kind}`, icon: "stars", go: { type: "record_new", kind, file, name: typed ?? undefined },
                        title: `Open a new ${kind} with Fill with AI reading this document — you tick each detail and save. People's names on it are never filled in.`,
                    }));
                    const who = readCompany ? `${readCompany} isn't in CRX` : "The company on it isn't in CRX";
                    return {
                        message: `This is a ${what}${until}. ${who} — start it as a new ${starts.join(" or ")} (the form reads this document), or type who it's for.`,
                        actions: [...newOnes, ...actions], usedReader, awaitsTarget,
                    };
                }
            }
            if (actions.length) return { message: `This is a ${what}${until}.`, actions, usedReader, targeted: true };
            return { message: `This is a ${what}. Who is it for? Type the provider or client name, or a mission reference (e.g. "MSN-0142").`, actions: [], awaitsTarget, usedReader };
        }
        default:
            if (read.details && (read.kind === "trip_details" || read.trip)) return { message: "", actions: [], chat: { question, details: read.details }, usedReader };
            return { message: `This is ${read.title ? `a ${read.title}` : "a document I can't act on"}. Say what to do with it, or open the record it belongs to and attach it there.`, actions: [], usedReader };
    }
};

/**
 * Run the action the person clicked: hand the file to the page that uses it
 * and go there (or open the price-list picker). Nothing is saved.
 */
export const runAttachGo = (go: AttachGo, navigate: (to: string) => void): void => {
    switch (go.type) {
        case "contact":
            handOverDraft("contact_paste", 0, { text: go.text, file: go.file, organizationName: go.organizationName });
            navigate("/contact");
            return;
        case "price_list_text":
            if (go.sheet && go.recordId) {
                handOverPriceList({ sheet: go.sheet, recordId: go.recordId, text: go.text });
                navigate(`/provider/${go.recordId}?tab=services`);
            } else {
                startPriceList({ text: go.text });
            }
            return;
        case "lead":
            handOverDraft("lead_paste", 0, { text: go.text });
            navigate("/lead");
            return;
        case "record_update":
            handOverDraft("record_paste", go.id, { text: go.text, file: go.file });
            navigate(`/${go.kind}/${go.id}`);
            return;
        case "fleet_text":
            handOverDraft("fleet_list", go.providerId, { text: go.text });
            navigate(`/provider/${go.providerId}?tab=assets`);
            return;
        case "fleet_file":
            handOverDraft("fleet_list", go.providerId, { file: go.file, items: go.items });
            navigate(`/provider/${go.providerId}?tab=assets`);
            return;
        case "price_list":
            if (go.providerId) {
                handOverPriceList({ sheet: "provider", recordId: go.providerId, file: go.file, lines: go.lines, places: go.places });
                navigate(`/provider/${go.providerId}?tab=services`);
            } else {
                startPriceList({ file: go.file, lines: go.lines });
            }
            return;
        case "record_new":
            queueAfterCreate(go.kind, go.then ?? []);
            handOverDraft("record_paste", 0, { text: go.text, file: go.file, name: go.name, address: go.address, notes: go.notes, phone: go.phone, email: go.email, website: go.website });
            navigate(`/${go.kind}`);
            return;
        case "booking":
            handOverDraft("booking", go.missionId, { file: go.file });
            navigate(`/mission/${go.missionId}?tab=itineraries`);
            return;
        case "document":
            handOverDraft(go.target.kind === "mission" ? "document_mission" : "document_org", go.target.id, { row: go.row });
            navigate(`/${go.target.kind}/${go.target.id}`);
            return;
        case "link":
            navigate(go.to);
            return;
    }
};
